import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSettings } from '@wu/widget-core';
import { isNearValue } from '../../../../helpers';

interface FloatingContextValue {
  height: number;
  setHeight: (height: number) => void;
  setMinimize: (minimize: boolean) => void;
  minimizePlayer: boolean;
}

const FloatingContext = createContext<FloatingContextValue>({
  height: 0,
  setHeight: () => {},
  setMinimize: () => {},
  minimizePlayer: false
});

export const useFloating = () => useContext(FloatingContext);

export function FloatingProvider({ children }: PropsWithChildren) {
  const [height, setHeight] = useState(0);

  const { minimizeButton = false, minimizeByDefault = false } = useSettings();
  const [minimizePlayer, setMinimize] = useState(
    minimizeButton && minimizeByDefault
  );

  useEffect(() => {
    setMinimize(minimizeButton && minimizeByDefault);
  }, [minimizeByDefault, minimizeButton]);

  const checkAndSetHeight = useCallback((newHeight: number) => {
    setHeight((prevHeight) =>
      !isNearValue(prevHeight, newHeight) ? newHeight : prevHeight
    );
  }, []);

  const value = useMemo(
    () => ({
      height,
      setHeight: checkAndSetHeight,
      setMinimize,
      minimizePlayer
    }),
    [checkAndSetHeight, height, minimizePlayer]
  );

  return (
    <FloatingContext.Provider value={value}>
      {children}
    </FloatingContext.Provider>
  );
}
